/* PLUMPLO — background removal adapter, PRODUCTION provider.
 *
 * Same seam as plumplo-bgremove.js (the development provider), same contract:
 *
 *   removeBackground(img: HTMLImageElement, { maxSide?, timeout? })
 *     → { canvas, provider, real }        canvas: RGBA cutout, alpha = subject matte
 *                                          real: false → Creator stops before geometry
 *   dispatches `plumplo-bg-status` events on `document` for UI status.
 *
 * What differs: no model runs in the browser. The ORIGINAL upload bytes are posted to
 * the PLUMPLO design service (a Cloudflare Worker, see shopify/worker/), which holds
 * the Photoroom API key, stores the original + the processed cutout + a design record,
 * and returns the cutout PNG plus an opaque design id. The key never reaches the
 * browser; the browser never talks to Photoroom directly.
 *
 * Additions that do not change the contract:
 *   - the result also carries `designId` (string) when the service issued one, and the
 *     `done` status event carries the same `designId`. The theme writes it into the
 *     cart line as `_design_id`. Nothing downstream needs it to build the pillow.
 *   - `PlumploBG.storeSupplied(img)` — for uploads that are already cut out (the
 *     Creator skips removeBackground for those), so a design record still exists.
 *
 * Configuration (set by the theme before this file runs):
 *   window.PLUMPLO_BG_ENDPOINT = 'https://<worker-host>'   base URL, no trailing slash
 *
 * Any failure (no endpoint, network, non-2xx, timeout, undecodable PNG) returns
 * `real: false` with the untouched image, and the Creator stops exactly as it does
 * for the development fallback. It never pretends.
 */
(function () {
  const PROVIDER = 'photoroom';

  function status(stage, detail) {
    document.dispatchEvent(new CustomEvent('plumplo-bg-status', {
      detail: Object.assign({ stage }, detail || {})
    }));
  }

  function endpoint() {
    const e = window.PLUMPLO_BG_ENDPOINT;
    /* https only — except localhost, so the provider can be exercised against a local
       mock of the service during development. */
    const ok = typeof e === 'string' && (/^https:\/\//.test(e) || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(e));
    return ok ? e.replace(/\/+$/, '') : null;
  }

  function canvasFrom(img, maxSide) {
    const s = Math.min(maxSide / img.width, maxSide / img.height, 1);
    const w = Math.max(1, Math.round(img.width * s));
    const h = Math.max(1, Math.round(img.height * s));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    cv.getContext('2d').drawImage(img, 0, 0, w, h);
    return cv;
  }

  /* The bytes the customer actually chose — not a canvas re-encode. img.src is the
     object URL the Creator made from the File, so fetch() hands back the original. */
  async function originalBlob(img) {
    const res = await fetch(img.src);
    if (!res.ok) throw new Error('could not read the upload');
    const blob = await res.blob();
    if (/^image\//.test(blob.type)) return blob;
    /* Some Android gallery apps hand over a photo with no type at all (or a generic one). Name it
       from its own first bytes, exactly as the design service checks them, so a real photo is not
       turned away for a missing label. Anything unrecognised is still refused, as before. */
    const sniffed = sniffImageType(new Uint8Array(await blob.slice(0, 16).arrayBuffer()));
    if (!sniffed) throw new Error('upload is not an image');
    return new Blob([blob], { type: sniffed });
  }
  /* Mirrors sniffImageType() in the Worker (shopify/worker/src/index.js). */
  const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1'];
  function sniffImageType(b) {
    if (b.length < 12) return null;
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47 && b[4] === 0x0D && b[5] === 0x0A && b[6] === 0x1A && b[7] === 0x0A) return 'image/png';
    if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'image/jpeg';
    const ascii = (i, n) => String.fromCharCode.apply(null, b.subarray(i, i + n));
    if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'image/webp';
    if (ascii(4, 4) === 'ftyp' && HEIF_BRANDS.indexOf(ascii(8, 4).toLowerCase()) !== -1) return 'image/heic';
    return null;
  }

  /* Only very large photos are resized before sending (decided 27 Sep 2026). Anything up to
     4500 px on its long side (a normal 12 MP phone photo is ~4032) and under 15 MB goes as the
     customer's exact bytes, as before. Bigger ones (48-50 MP cameras) are redrawn at most 4500 px
     long, capped at 16 MP so iPhone canvases can hold them, and saved as a JPEG at 0.92.
     Resizing is best effort: if the browser can't do it, the original bytes are sent unchanged. */
  const SEND_MAX_SIDE = 4500, SEND_MAX_PIXELS = 16000000, SEND_MAX_BYTES = 15 * 1024 * 1024;
  async function sendableBlob(img, blob) {
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    if (!w || !h) return blob;
    if (Math.max(w, h) <= SEND_MAX_SIDE && blob.size <= SEND_MAX_BYTES) return blob;
    const s = Math.min(1, SEND_MAX_SIDE / Math.max(w, h), Math.sqrt(SEND_MAX_PIXELS / (w * h)));
    try {
      const cv = document.createElement('canvas');
      cv.width = Math.max(1, Math.round(w * s)); cv.height = Math.max(1, Math.round(h * s));
      const ctx = cv.getContext('2d');
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, cv.width, cv.height);
      const out = await new Promise((ok) => cv.toBlob(ok, 'image/jpeg', 0.92));
      cv.width = cv.height = 0;                      // release the canvas memory promptly
      return out && out.size ? out : blob;
    } catch (e) { return blob; }
  }

  const withTimeout = (p, ms, label) => Promise.race([
    p,
    new Promise((_, rej) => setTimeout(() => rej(new Error(label + ' timed out after ' + ms + 'ms')), ms))
  ]);

  async function post(path, blob, signal) {
    const base = endpoint();
    if (!base) throw new Error('design service not configured');
    const fd = new FormData();
    fd.append('image', blob, 'upload' + extFor(blob.type));
    const res = await fetch(base + path, { method: 'POST', body: fd, signal, credentials: 'omit' });
    if (!res.ok) {
      let msg = 'design service error ' + res.status;
      try { const j = await res.json(); if (j && j.error) msg = j.error; } catch (e) { /* keep status text */ }
      throw new Error(msg);
    }
    return res;
  }

  function extFor(type) {
    return type === 'image/png' ? '.png' : type === 'image/webp' ? '.webp' : type === 'image/heic' ? '.heic' : '.jpg';
  }

  function decode(blob) {
    return new Promise((ok, err) => {
      const url = URL.createObjectURL(blob);
      const im = new Image();
      im.onload = () => { URL.revokeObjectURL(url); ok(im); };
      im.onerror = () => { URL.revokeObjectURL(url); err(new Error('cutout could not be decoded')); };
      im.src = url;
    });
  }

  /* ---- the provider ---- */
  async function viaService(img, maxSide, signal) {
    status('uploading');
    const blob = await sendableBlob(img, await originalBlob(img));
    const res = await post('/cutout', blob, signal);
    const designId = res.headers.get('x-plumplo-design-id') || '';
    status('segmenting', { designId });
    const png = await res.blob();
    if (png.type !== 'image/png') throw new Error('design service returned ' + (png.type || 'no image'));
    const cut = await decode(png);
    const canvas = canvasFrom(cut, maxSide);       // RGBA; transparent stays transparent
    return { canvas, provider: PROVIDER, real: true, designId };
  }

  /* The only entry point the creator uses. */
  async function removeBackground(img, opts) {
    const maxSide = (opts && opts.maxSide) || 1024;
    const budget = (opts && opts.timeout) || 90000;   // 45 s was too short for big photos on mobile data
    const ctl = new AbortController();
    try {
      const out = await withTimeout(viaService(img, maxSide, ctl.signal), budget, 'background removal');
      status('done', { provider: out.provider, real: true, designId: out.designId });
      return out;
    } catch (err) {
      ctl.abort();
      const out = { canvas: canvasFrom(img, maxSide), provider: 'none', real: false };
      status('done', { provider: out.provider, real: false, error: String(err && err.message || err) });
      return out;
    }
  }

  /* Pre-cut uploads never pass through removeBackground (the Creator uses them as-is),
     but the order still needs a design record holding the artwork. Stores the original
     only; returns the design id or null. Never throws. */
  async function storeSupplied(img, opts) {
    const budget = (opts && opts.timeout) || 30000;
    const ctl = new AbortController();
    try {
      status('uploading');
      const blob = await originalBlob(img);
      const res = await withTimeout(post('/upload', blob, ctl.signal), budget, 'upload');
      const j = await res.json();
      status('stored', { designId: j.designId });
      return j.designId || null;
    } catch (err) {
      ctl.abort();
      status('store-failed', { error: String(err && err.message || err) });
      return null;
    }
  }

  window.PlumploBG = { removeBackground, storeSupplied, PROVIDER };
})();
